"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { MapClient, MapProps, MarkerProps } from "../../../types";
import { type Map as MapboxMap, Marker, Popup } from "mapbox-gl";
import ClickAwayListener from "react-click-away-listener";

export function MarkerComponent<T>({
  item,
  index,
  map,
  parentProps,
}: Omit<MarkerProps<T>, "isActive" | "markerElementRef"> & {
  map: MapClient<MapboxMap> | null;
  parentProps: MapProps<T, MapboxMap>;
}) {
  const markerRef = useRef<Marker | null>(null);
  const markerElement = useRef<HTMLDivElement | null>(null);
  const popupElement = useRef<HTMLDivElement | null>(null);

  const [active, setActive] = useState(false);

  const coords = parentProps.coordsGetter(item);

  function closePopup() {
    markerRef.current?.getPopup()?.remove();
  }

  // Create marker and popup once on mount. Using [map] ensures the effect
  // only runs when the map instance is ready and never again — this prevents
  // the marker (and its popup) from being destroyed/recreated when
  // supercluster produces new wrapper objects on zoom, which was causing
  // open popups to close unexpectedly.
  useEffect(() => {
    if (!map || !markerElement.current) return;

    const marker = new Marker({
      element: markerElement.current,
    })
      .setLngLat({ lat: coords.lat, lng: coords.lng })
      .addTo(map.getRawClient());

    markerRef.current = marker;

    if (parentProps.renderPopup && popupElement.current) {
      const popup = new Popup({
        closeButton: parentProps.settings?.popup?.closeButton,
        closeOnClick: parentProps.settings?.popup?.closeOnClick,
        closeOnMove: parentProps.settings?.popup?.closeOnMove,
        maxWidth: parentProps.settings?.popup?.maxWidth,
        offset: parentProps.settings?.popup?.offset ?? 14,
        className: "venturu-map-popup",
      })
        .setDOMContent(popupElement.current)
        .on("open", () => {
          setActive(true);
          // Trigger a nil position update so popup position gets recomputed.
          map.getRawClient().panBy(
            [0, 0],
            {},
            { popupUpdate: true },
          );
        })
        .on("close", () => {
          setActive(false);
        });

      marker.setPopup(popup);
    }

    return () => {
      marker.remove();
    };
  }, [map]);

  // Update position when coordinates change without recreating the marker.
  useEffect(() => {
    markerRef.current?.setLngLat({ lat: coords.lat, lng: coords.lng });
  }, [coords.lat, coords.lng]);

  // React re-renders place these nodes back in the component tree. Put them
  // back on the map before paint so a marker cannot stretch over its popup.
  useLayoutEffect(() => {
    const element = markerElement.current;
    const rawMap = map?.getRawClient();
    if (!element || !rawMap) return;

    const container = rawMap.getCanvasContainer();
    if (element.parentNode !== container) {
      container.appendChild(element);
    }

    const popupNode = popupElement.current;
    const popupRoot = markerRef.current?.getPopup()?.getElement();
    const content = popupRoot?.querySelector(".mapboxgl-popup-content");
    if (popupNode && content && popupNode.parentNode !== content) {
      content.appendChild(popupNode);
    }
  });

  if (!parentProps.renderMarker) {
    return null;
  }

  return (
    <ClickAwayListener onClickAway={closePopup}>
      <div>
        {parentProps.renderMarker({
          markerElementRef: markerElement,
          item,
          index,
          isActive: active,
        })}

        <div ref={popupElement}>
          {parentProps.renderPopup?.({ item, index, isActive: active })}
        </div>
      </div>
    </ClickAwayListener>
  );
}

MarkerComponent.displayName = "MarkerComponent";
